'use client';

import { forwardRef, useMemo } from 'react';

import { css, cx, splitResponsiveVariant } from '@/styled-system/css';

import { normalizeResponsiveStyles } from '../../utils/normalize-responsive-styles';
import { Slot } from '../slot';
import { ButtonGroupContext } from './context/button-group.context';
import { buttonGroupRecipe } from './styles/button-group.styles';
import type { ButtonGroupPropsInterface } from './types/button-group.types';

/**
 * Be sure to add an appropriate
 * [aria-label](https://www.w3.org/TR/wai-aria-1.2/#aria-label) for assistive technologies.
 *
 * @related ['Button', 'LoadingButton', 'TextButton', 'ToggleButton']
 * @since 5.0.0
 */

export const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupPropsInterface>(
  (props: ButtonGroupPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      tone,
      emphasis,
      conjoined = false,
      children,
      className,
      css: cssProp,
      fluid = false,
      reverse = false,
      role,
      orientation = 'horizontal',
      size,
      ...rest
    } = props;

    const reverseForStyles = normalizeResponsiveStyles(reverse, orientation);
    const conjoinedForStyles = normalizeResponsiveStyles(conjoined, orientation);
    const responsiveFluid = splitResponsiveVariant('fluid', fluid);
    const responsiveReverse = splitResponsiveVariant('reverse', reverseForStyles);
    const responsiveOrientation = splitResponsiveVariant('orientation', orientation);
    const responsiveConjoined = splitResponsiveVariant('conjoined', conjoinedForStyles);

    const styles = css(
      buttonGroupRecipe.raw({
        ...responsiveConjoined,
        ...responsiveFluid,
        ...responsiveReverse,
        ...responsiveOrientation,
      }),
      cssProp,
    );
    const Component = asChild ? Slot : 'div';

    const resolvedRole = asChild ? role : (role ?? 'group');

    const contextValue = useMemo(
      () => ({ tone, emphasis, fluid, size }),
      [tone, emphasis, fluid, size],
    );

    return (
      <ButtonGroupContext.Provider value={contextValue}>
        <Component
          ref={forwardedRef}
          data-c11n-component="ButtonGroup"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'c11n_button-group', styles, className)}
          role={resolvedRole}
          {...rest}
        >
          {children}
        </Component>
      </ButtonGroupContext.Provider>
    );
  },
);

ButtonGroup.displayName = 'ButtonGroup';
